import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/shallow";
import {
  useCompanyFilters,
  useCompanyFilterActions,
  useDebouncedAutoApply,
} from "./hooks";
import {
  useCompanyFiltersStore,
  useCompanyFilterLookups,
} from "./provider/CompanyFilterProvider";
import {
  collectModuleChips,
  getModuleCount,
  type ModuleKey,
  type ModulesSlices,
} from "./modules";
import { useTargetOrganizationsHydration } from "./modules/targetOrganizations";
import { useIntentTopicsHydration } from "./modules/intent/useIntentTopicsHydration";
import { useIntentCollectionHydration } from "./modules/intent/useIntentCollectionHydration";
import type { CompanyFilters, FilterChip, FilterKey } from "./types";

export type CompanyFiltersController = {
  slices: ModulesSlices;
  appliedFilters: CompanyFilters | undefined;
  hasUnsavedChanges: boolean;
  /** True when draft edits have not yet been applied (manual confirm flow). */
  hasPendingChanges: boolean;
  chips: FilterChip[];
  getFilterCount: (key: FilterKey) => number;
  setSlice: <K extends ModuleKey>(key: K, slice: ModulesSlices[K]) => void;
  /** Apply the current draft to applied (confirm filters). */
  applyDraft: () => void;
  /** Discard draft edits, restoring the last applied state. */
  revertDraftToApplied: () => void;
  /** Clear only the draft filters, keeping applied filters until confirmed. */
  clearDraft: () => void;
  clearAll: () => void;
  hydrateFromCompanyFilters: (filters: CompanyFilters | undefined) => void;
  serializeAppliedToJson: () => string;
  markAsPersisted: () => void;
};

export type UseCompanyFiltersControllerOptions = {
  autoApply?: boolean;
};

export function useCompanyFiltersController(
  options: UseCompanyFiltersControllerOptions = {}
): CompanyFiltersController {
  useDebouncedAutoApply(options.autoApply !== false);

  // Hydrate targetOrganizations cache when IDs are present but details are missing.
  // This ensures chips display correctly even before the filter panel is opened.
  useTargetOrganizationsHydration();
  useIntentTopicsHydration();
  useIntentCollectionHydration();

  const { appliedFilters, hasUnsavedChanges, hasPendingChanges } =
    useCompanyFilters();

  const slices = useCompanyFiltersStore(useShallow((s) => s.slices));
  const {
    setSlice,
    applyDraft,
    revertDraftToApplied,
    hydrateFromCompanyFilters,
    serializeAppliedToJson,
    markAsPersisted,
    clearDraft,
    clearAll,
  } = useCompanyFilterActions();

  const lookups = useCompanyFilterLookups();

  const chips = useMemo<FilterChip[]>(() => {
    return collectModuleChips(slices, setSlice, lookups);
  }, [slices, setSlice, lookups]);

  const getFilterCount = useCallback(
    (key: FilterKey): number => {
      return getModuleCount(key as ModuleKey, slices);
    },
    [slices]
  );

  return {
    slices,
    appliedFilters,
    hasUnsavedChanges,
    hasPendingChanges,
    chips,
    getFilterCount,
    setSlice,
    applyDraft,
    revertDraftToApplied,
    clearDraft,
    clearAll,
    hydrateFromCompanyFilters,
    serializeAppliedToJson,
    markAsPersisted,
  };
}
